import { Box, Button, Link as MuiLink, Paper, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import {
  ClassCard,
  EmptyState,
  HeroBand,
  InlineAlert,
  JoinCodeCard,
  Loader,
  StatusChip,
} from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { SchoolClass } from "src/models";
import type {
  SessionDetail,
  SessionEntry,
  SessionStatus,
  Station,
} from "src/models/sessions";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { useGetMyClassesQuery } from "src/store/reducers/classes/classesApiSlice";
import {
  useGetReviewQuery,
  useGetSessionQuery,
  useListSessionsQuery,
} from "src/store/reducers/sessions/sessionsApiSlice";

/** Session dates are calendar days ("YYYY-MM-DD"); parse them as local dates, not UTC midnight. */
const parseSessionDate = (value: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(value);
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const greetingKey = (hour: number) =>
  hour < 12
    ? "today.greetingMorning"
    : hour < 18
      ? "today.greetingAfternoon"
      : "today.greetingEvening";

const liveEntries = (entries: SessionEntry[] | undefined) =>
  (entries ?? []).filter((e) => !e.supersededBy);

/** Students with at least one current entry at the station. */
const stationDone = (session: SessionDetail, station: Station) =>
  new Set(
    liveEntries(session.entries)
      .filter((e) =>
        e.stationId
          ? e.stationId === station.id
          : e.eventId === station.eventId,
      )
      .map((e) => e.studentId),
  ).size;

/** Students with at least one current entry anywhere in the session. */
const testedCount = (session: SessionDetail) =>
  new Set(liveEntries(session.entries).map((e) => e.studentId)).size;

/** The design's white pill on the hero: ink text on the surface color, never the red action color. */
const heroButtonSx = {
  bgcolor: "background.paper",
  color: "text.primary",
  fontSize: 15,
  fontWeight: 800,
  minHeight: 48,
  px: 2.75,
  "&:hover": { bgcolor: "background.paper", transform: "translateY(-1px)" },
} as const;

/**
 * A session in review (design "Needs you" card). The sessions list does not carry flag counts,
 * so each card reads its own session's review view for the open flag count.
 */
const NeedsYouCard = ({ session }: { session: SessionDetail }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const { data } = useGetReviewQuery(session.id);
  const openFlags = data
    ? data.flags.filter((f) => f.status === "open").length
    : undefined;
  return (
    <Box
      component={RouterLink}
      to={`/grading/sessions/${session.id}/review`}
      aria-labelledby={titleId}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2.25,
        borderRadius: `${radius.card}px`,
        bgcolor: theme.palette.tokens.warningBg,
        border: `1.5px solid ${theme.palette.warningLine}`,
        color: theme.palette.text.primary,
        textDecoration: "none",
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.focusRing}`,
          outlineOffset: 2,
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <StatusChip status="review" />
        {openFlags !== undefined && openFlags > 0 ? (
          <Typography
            variant="overline"
            component="span"
            sx={{ color: "warning.dark", lineHeight: 1 }}
          >
            {`${t("review.flags")} · ${openFlags}`}
          </Typography>
        ) : null}
      </Box>
      <Typography
        id={titleId}
        component="h3"
        sx={{ m: 0, fontSize: 16, fontWeight: 800 }}
      >
        {t("today.readyToFinalize", { name: session.name })}
      </Typography>
      <Typography
        sx={{ fontSize: 13, lineHeight: 1.4, color: "text.secondary" }}
      >
        {t("today.finalizeBody")}
      </Typography>
    </Box>
  );
};

/** Most "Needs you" cards shown; each one fetches a review view. */
const NEEDS_YOU_LIMIT = 5;

const STATUS_ORDER: Record<SessionStatus, number> = {
  open: 0,
  review: 1,
  planned: 2,
  finalized: 3,
};

/** The session that best describes a class today: live, then in review, then next planned, then latest finalized. */
const sessionForClass = (sessions: SessionDetail[], classId: string) =>
  sessions
    .filter((s) => s.classId === classId)
    .sort((a, b) => {
      const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      if (byStatus !== 0) return byStatus;
      const diff =
        parseSessionDate(a.date).getTime() - parseSessionDate(b.date).getTime();
      return a.status === "finalized" ? -diff : diff;
    })[0];

const arrowLink = {
  fontSize: 13,
  fontWeight: 700,
  color: "primaryInk",
} as const;

/** Signed-in landing page: greeting, the live or next session, sessions waiting on review and the teacher's classes. */
const TodayPage = () => {
  const { t, i18n } = useTranslation();
  const user = useSelector(selectCurrentUser);
  const { hasPermissions } = usePermissions();
  const canPlan = hasPermissions(
    permissions.CREATE_SESSIONS.action,
    permissions.CREATE_SESSIONS.subject,
  );

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    isError: sessionsError,
  } = useListSessionsQuery();
  // Admins don't teach classes (no `classes:own`); they reach every class from Classes instead.
  const teachesClasses = hasPermissions(
    permissions.READ_OWN_CLASSES.action,
    permissions.READ_OWN_CLASSES.subject,
  );
  const {
    data: classes,
    isLoading: classesLoading,
    isError: classesError,
  } = useGetMyClassesQuery(undefined, { skip: !teachesClasses });

  const sessions = useMemo(() => sessionsData?.sessions ?? [], [sessionsData]);
  const openSessions = sessions.filter((s) => s.status === "open");
  const reviewSessions = sessions.filter((s) => s.status === "review");
  // Each card reads its own review view, so cap the fan-out: the most recent few, then a link.
  const reviewCards = [...reviewSessions]
    .sort(
      (a, b) =>
        parseSessionDate(b.date).getTime() - parseSessionDate(a.date).getTime(),
    )
    .slice(0, NEEDS_YOU_LIMIT);
  const liveSummary = openSessions[0];
  const upcoming = useMemo(() => {
    const today = startOfToday().getTime();
    const planned = sessions
      .filter((s) => s.status === "planned")
      .sort(
        (a, b) =>
          parseSessionDate(a.date).getTime() -
          parseSessionDate(b.date).getTime(),
      );
    const future = planned.filter(
      (s) => parseSessionDate(s.date).getTime() >= today,
    );
    return future.length > 0 ? future : planned;
  }, [sessions]);
  const nextPlanned = upcoming[0];

  const { data: liveDetail } = useGetSessionQuery(
    liveSummary ? liveSummary.id : skipToken,
  );
  const live = liveDetail ?? liveSummary;

  useEffect(() => {
    // PageHeader titles the tab with the greeting; the tab reads "Today" instead.
    document.title = `${t("nav.todayTitle")} · ${t("app.name")}`;
  }, [t]);

  const dateFormat = (options: Intl.DateTimeFormatOptions, date: Date) =>
    new Intl.DateTimeFormat(i18n.language, options).format(date);
  const formatSessionDate = (value: string) =>
    dateFormat(
      { weekday: "short", month: "short", day: "numeric" },
      parseSessionDate(value),
    );

  if (sessionsLoading || classesLoading) return <Loader />;

  const firstName = user?.firstName ?? "";
  const windowSource = live ?? nextPlanned;
  const windowName = windowSource
    ? t(`sessions.window.${windowSource.window}`)
    : null;
  const eyebrow = [
    dateFormat({ weekday: "long", month: "long", day: "numeric" }, new Date()),
    windowName
      ? t("today.windowEyebrow", {
          window: windowName,
          defaultValue: "{{window}} window",
        })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const summary = [
    openSessions.length > 0
      ? t("today.summary_live", { count: openSessions.length })
      : null,
    reviewSessions.length > 0
      ? t("today.summary_review", { count: reviewSessions.length })
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  const planButton = (variant: "contained" | "outlined") =>
    canPlan ? (
      <Button
        component={RouterLink}
        to="/grading/sessions/plan"
        variant={variant}
        color={variant === "outlined" ? "secondary" : "primary"}
      >
        {t("sessions.plan")}
      </Button>
    ) : null;

  const hero = (() => {
    if (live) {
      const total = live.rosterSnapshot?.length ?? 0;
      const stations = [...(live.stations ?? [])].sort(
        (a, b) => a.order - b.order,
      );
      const hasCounts = Array.isArray(live.entries);
      return (
        <HeroBand
          component="h2"
          eyebrow={
            <>
              <StatusChip status="open" />
              <span>
                {[
                  t(`sessions.window.${live.window}`),
                  formatSessionDate(live.date),
                ].join(" · ")}
              </span>
            </>
          }
          title={t("today.testingNow", { name: live.name })}
          aside={
            <>
              {live.joinCode ? (
                <JoinCodeCard
                  variant="hero"
                  sessionId={live.id}
                  code={live.joinCode}
                  canManage={false}
                />
              ) : null}
              <Button
                component={RouterLink}
                to={`/grading/sessions/${live.id}`}
                variant="contained"
                sx={heroButtonSx}
              >
                {`${t("today.openMissionControl")} →`}
              </Button>
            </>
          }
        >
          {stations.length > 0 && hasCounts ? (
            <Box
              sx={{
                display: "grid",
                gap: 1.75,
                maxWidth: 520,
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              }}
            >
              {stations.map((station) => {
                const done = stationDone(live, station);
                const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
                const eventName = t(`events.${station.eventId}`, {
                  defaultValue: station.label,
                });
                return (
                  <Box
                    key={station.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        color: "heroMuted",
                      }}
                    >
                      {t(`categories.${station.category}`)}
                    </Typography>
                    <Box
                      sx={{
                        fontSize: 26,
                        fontWeight: 800,
                        lineHeight: 1,
                        fontStretch: fontStretch.condensed,
                      }}
                    >
                      {done}
                      <Box
                        component="span"
                        sx={{ fontSize: 15, color: "heroMuted" }}
                      >
                        {`/${total}`}
                      </Box>
                    </Box>
                    <Box
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={total}
                      aria-valuenow={done}
                      aria-label={t("sessions.stationProgress", {
                        event: eventName,
                        defaultValue: "{{event}} progress",
                      })}
                      sx={(theme) => ({
                        height: 6,
                        borderRadius: `${radius.pill}px`,
                        bgcolor: theme.palette.tokens.heroMuted,
                        opacity: 0.9,
                        overflow: "hidden",
                      })}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${pct}%`,
                          bgcolor: "heroInk",
                          borderRadius: `${radius.pill}px`,
                        }}
                      />
                    </Box>
                    <Typography
                      component="span"
                      sx={{ fontSize: 12, color: "heroMuted" }}
                    >
                      {eventName}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </HeroBand>
      );
    }
    if (nextPlanned) {
      return (
        <HeroBand
          component="h2"
          eyebrow={<StatusChip status="planned" />}
          title={t("today.nextUp", {
            name: nextPlanned.name,
            date: formatSessionDate(nextPlanned.date),
          })}
          aside={
            <Button
              component={RouterLink}
              to={`/grading/sessions/${nextPlanned.id}`}
              variant="contained"
              sx={heroButtonSx}
            >
              {t("sessions.open")}
            </Button>
          }
        />
      );
    }
    return (
      <HeroBand component="h2" title={t("sessions.today")}>
        <Box
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            borderRadius: `${radius.card}px`,
          }}
        >
          <EmptyState
            headingLevel={3}
            title={t("today.nothingPlanned")}
            action={planButton("contained")}
          />
        </Box>
      </HeroBand>
    );
  })();

  const classList: SchoolClass[] = classes ?? [];
  const loadErrorText = t("console.loadErrorRetry", {
    defaultValue: "Could not load. Refresh to try again.",
  });

  const sectionHeading = (id: string, text: string) => (
    <Typography id={id} variant="h5" component="h2" sx={{ m: 0 }}>
      {text}
    </Typography>
  );

  const windowCard =
    windowName && (upcoming.length > 0 || live) ? (
      <Paper
        component="section"
        aria-label={t("today.testingWindow", {
          window: windowName,
          defaultValue: "{{window}} testing window",
        })}
        sx={{
          p: 2.25,
          borderRadius: `${radius.card}px`,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
          {t("today.testingWindow", {
            window: windowName,
            defaultValue: "{{window}} testing window",
          })}
        </Typography>
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {upcoming.slice(0, 4).map((s) => (
            <Box
              component="li"
              key={s.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1.5,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <MuiLink
                component={RouterLink}
                to={`/grading/sessions/${s.id}`}
                underline="hover"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {s.name}
              </MuiLink>
              <Box component="span" sx={{ color: "text.secondary" }}>
                {formatSessionDate(s.date)}
              </Box>
            </Box>
          ))}
          <Box
            component="li"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <span>
              {t("today.retestWindow", { defaultValue: "Retest window" })}
            </span>
            <Box component="span" sx={{ color: "text.secondary" }}>
              {t("today.retestLater", { defaultValue: "8–12 weeks later" })}
            </Box>
          </Box>
        </Box>
      </Paper>
    ) : null;

  const needsYou =
    reviewSessions.length > 0 || windowCard ? (
      <Box
        component="section"
        aria-labelledby="today-needs-you"
        sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
      >
        {sectionHeading("today-needs-you", t("today.needsYou"))}
        {reviewCards.map((session) => (
          <NeedsYouCard key={session.id} session={session} />
        ))}
        {reviewSessions.length > reviewCards.length ? (
          <MuiLink
            component={RouterLink}
            to="/grading/sessions"
            underline="hover"
            sx={{ ...arrowLink, alignSelf: "flex-start" }}
          >
            {`${t("today.viewAllInReview", {
              defaultValue: "View all sessions in review",
            })} →`}
          </MuiLink>
        ) : null}
        {windowCard}
      </Box>
    ) : null;

  const gradeLabel = (c: SchoolClass) =>
    c.gradeLevel
      ? `${t("classes.grade")} ${c.gradeLevel}`
      : c.period
        ? `${t("console.period")} ${c.period}`
        : t("classes.grade");

  const myClasses = (
    <Box
      component="section"
      aria-labelledby="today-my-classes"
      sx={{ display: "flex", flexDirection: "column", gap: 1.75, minWidth: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {sectionHeading("today-my-classes", t("today.myClasses"))}
        <MuiLink
          component={RouterLink}
          to="/grading/classes"
          underline="hover"
          sx={arrowLink}
        >
          {`${t("today.allClasses")} →`}
        </MuiLink>
      </Box>
      {classesError ? (
        <InlineAlert severity="error">{loadErrorText}</InlineAlert>
      ) : classList.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("classes.noneAssigned")}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.75,
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          }}
        >
          {classList.map((c) => {
            const session = sessionForClass(sessions, c.id);
            const next = sessions.find(
              (s) => s.classId === c.id && s.status === "planned",
            );
            const nextSessionLabel =
              next && next.id !== session?.id
                ? `${t("sessions.status.planned")} · ${formatSessionDate(next.date)}`
                : undefined;
            // Only the fetched live session carries entries; the sessions list does not, so any
            // other class shows no tested count rather than a made-up "0 of N".
            const measured =
              session &&
              liveDetail &&
              session.id === liveDetail.id &&
              Array.isArray(liveDetail.entries)
                ? liveDetail
                : undefined;
            const rosterSize = session?.rosterSnapshot?.length ?? 0;
            return (
              <ClassCard
                key={c.id}
                id={c.id}
                name={c.name}
                gradeLabel={gradeLabel(c)}
                year={c.schoolYear}
                studentCount={
                  measured
                    ? (measured.rosterSnapshot?.length ?? 0)
                    : rosterSize > 0
                      ? rosterSize
                      : undefined
                }
                testedCount={measured ? testedCount(measured) : undefined}
                status={
                  session?.status ??
                  (c.source === "manual" ? "manual" : "connected")
                }
                nextSessionLabel={nextSessionLabel}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
      <PageHeader
        uppercase={false}
        eyebrow={eyebrow}
        title={t(greetingKey(new Date().getHours()), { name: firstName })}
        description={summary || undefined}
        actions={
          <>
            {planButton("contained")}
            <Button
              component={RouterLink}
              to="/grading/sessions"
              variant="outlined"
              color="secondary"
            >
              {t("today.allSessions")}
            </Button>
          </>
        }
      />
      {/* Without the sessions list the hero would claim "Nothing planned"; show the error instead. */}
      {sessionsError ? (
        <InlineAlert severity="error">{loadErrorText}</InlineAlert>
      ) : (
        hero
      )}
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: "minmax(0, 1fr)",
          "@media (min-width: 1024px)":
            teachesClasses && needsYou
              ? { gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)" }
              : {},
        }}
      >
        {teachesClasses ? myClasses : null}
        {needsYou}
      </Box>
    </Box>
  );
};

export default TodayPage;
